import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, IconButton, Paper, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import { ProgressBar, StatusChip } from '../quotas/QuotaTable';
import { fieldErrors, wranglerError } from './errors';
import { IncreaseDialog, SetLimitDialog } from './LimitDialogs';

export const OVERALL_LOGIC = { match: 'all' };
const errorText = (err) => [wranglerError(err), ...fieldErrors(err)].join('\n');

/**
 * Create the survey's overall quota (design §8): a quota that counts every complete. Wrangler's
 * import doesn't copy Alchemer's overall quota (it isn't in Alchemer's quota list), so Ranger
 * creates it. It starts OFF, like every new quota.
 */
export const OverallQuotaDialog = ({ surveyId, onCreated, onClose }) => {
  const [name, setName] = useState('Overall quota');
  const [limit, setLimit] = useState('');
  const [isOn, setIsOn] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const valid = name.trim() !== '' && /^\d+$/.test(limit);

  const create = async () => {
    setPending(true);
    setError(null);
    try {
      const quota = { name: name.trim(), logic: OVERALL_LOGIC, original_limit: Number(limit), is_on: isOn };
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/quotas/`, { quotas: [quota] });
      onCreated(data.quotas[0]);
      onClose();
    } catch (err) {
      setError(errorText(err));
      setPending(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create the overall quota</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A total for the whole survey: it counts every complete. Use the same limit as Alchemer&apos;s Overall Quota.
          Exemption rules only let people past it if their &quot;Also allow past the overall quota&quot; box is ticked.
        </Typography>
        <TextField label="Name" size="small" fullWidth required value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />
        <TextField label="Limit" size="small" type="number" required value={limit} autoFocus
          onChange={(e) => setLimit(e.target.value.trim())} error={limit !== '' && !/^\d+$/.test(limit)} sx={{ width: 180 }} />
        <FormControlLabel sx={{ mt: 1.5, display: 'flex' }}
          control={<Checkbox checked={isOn} onChange={(e) => setIsOn(e.target.checked)} />} label="Switch it ON straight away" />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          {isOn ? 'It closes the survey to new completes once it fills.' : 'Left unticked, it starts OFF: it counts, but blocks nobody.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={!valid || pending} sx={{ textTransform: 'none' }}>
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const asProgress = (q) => ({ ...q, responses: q.complete_count, limit: q.current_limit, reserved: q.reserved_count });

/** The overall quota's own box above the list, as in Alchemer: completes, limit, increases, ON/OFF. */
export const OverallQuotaBox = ({ surveyId, quota, onChanged, onReload, onDeleted }) => {
  const [dialog, setDialog] = useState(null); // 'increase' | 'set-limit' | 'delete' | 'confirm-on'
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const change = quota.current_limit - quota.original_limit;

  const act = async (request, after) => {
    setPending(true);
    setError(null);
    try {
      const { data } = await request();
      after(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
      setDialog(null);
    }
  };
  const setOn = (isOn) => act(() => api.patch(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`, { is_on: isOn }),
    (data) => onChanged(data.quota));
  const remove = () => act(() => api.delete(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`), () => onDeleted(quota.id));
  const limitDone = () => { setDialog(null); onReload(); };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} aria-label="Overall quota">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>Overall quota</Typography>
          <Typography variant="body1" fontWeight={600}>{safeDecode(quota.name)}</Typography>
        </Box>
        <Tooltip title={quota.is_on ? 'Switch OFF' : 'Switch ON'}>
          <span>
            <Switch checked={quota.is_on} disabled={pending} inputProps={{ 'aria-label': 'Overall quota ON' }}
              onChange={(e) => (e.target.checked ? setDialog('confirm-on') : setOn(false))} />
          </span>
        </Tooltip>
        <Box>
          <Typography variant="h6" component="p" sx={{ lineHeight: 1.2 }}>
            {quota.complete_count.toLocaleString()} / {quota.current_limit.toLocaleString()}
            {change !== 0 && (
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}(original {quota.original_limit.toLocaleString()}, {change > 0 ? '+' : '−'}{Math.abs(change)})
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            completes · {quota.reserved_count} holding a place · counts every complete
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}><ProgressBar quota={asProgress(quota)} /></Box>
        {quota.closed && <StatusChip label="Closed" colour="error" title="Full, counting places held" />}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setDialog('increase')} disabled={pending}
            aria-label="Increase the overall quota" sx={{ textTransform: 'none' }}>Increase…</Button>
          <Button size="small" variant="outlined" onClick={() => setDialog('set-limit')} disabled={pending}
            aria-label="Set the overall quota's limit" sx={{ textTransform: 'none' }}>Set limit…</Button>
          <Tooltip title="Delete">
            <span>
              <IconButton size="small" aria-label="Delete the overall quota" onClick={() => setDialog('delete')} disabled={pending}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1.5, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>{error}</Alert>}

      {dialog === 'increase' && <IncreaseDialog surveyId={surveyId} quota={quota} onClose={() => setDialog(null)} onDone={limitDone} />}
      {dialog === 'set-limit' && <SetLimitDialog surveyId={surveyId} quota={quota} onClose={() => setDialog(null)} onDone={limitDone} />}
      <Dialog open={dialog === 'confirm-on'} onClose={() => setDialog(null)}>
        <DialogTitle>Switch ON the overall quota?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once {quota.current_limit.toLocaleString()} have completed (counting people holding a place), Wrangler turns
            everyone else away, unless an exemption rule lets them past the overall quota.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => setOn(true)} sx={{ textTransform: 'none' }}>Switch ON</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)}>
        <DialogTitle>Delete the overall quota?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its limit history goes with it. Respondents it would have blocked are let through. This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
